import { type Config } from '@common/clients/api/client';
import { type PlatformID } from '@common/clients/api/types.gen';

import { createProxyService } from './utils/createProxyService';
import { withUploadProgress } from './utils/withUploadProgress';
import { createApiClient, type CreateApiClientConfig } from './api-client';
import { createCrossPlatformApiClient } from './cross-platform-api-client';
import { type ApiFetchOptions } from './types';

// @note: these types need to remain in this file
// because ContextData contains reference to Sidebar
// that references icons which will cause build error
// in workspaces like @cms/api and @cms/cron where
// clients are used that import from ./types.ts
export interface ServiceArgs<T> {
    userContext?: CreateApiClientConfig['userContext'];
    contextData: CreateApiClientConfig['contextData'];
    isClientSide?: boolean;
    config?: T & ApiFetchOptions;
}

export interface ApiServiceArgs<T, C> extends ServiceArgs<C> {
    service: T;
}

/**
 * @description Creates the main api service object.
 * Usage in both @cms/admin and @cms/web to make api calls.
 * @returns The api service object.
 * @example
 * const { data, error } = await ApiService({
 *  contextData,
 *  userContext,
 *  isClientSide: true,
 *  config: {
 *    timeout: 10000,
 *  },
 *  service: apiService,
 * }).someGeneratedMethod({
 *  query: {
 *    id: 1,
 *  },
 * });
 */
export function ApiService<T extends object>({
    contextData,
    userContext,
    isClientSide = true,
    config,
    service,
    onUploadProgress,
}: ApiServiceArgs<T, Config> & { onUploadProgress?: (progress: number) => void }): T {
    /**
     * @description override the fetch function if onUploadProgress is provided
     * since fetch doesnt support progress events
     * @see: @common/clients/request/request-utils.ts
     */
    if (onUploadProgress) {
        config = {
            ...config,
            fetch: withUploadProgress(onUploadProgress),
        };
    }

    const client = createApiClient({ contextData, userContext, isClientSide, fetchOptions: config });

    return createProxyService<T>(client, service);
}

/**
 * @description Creates the cross platform api service object.
 * @example see example ApiService
 */
export function CrossPlatformApiService<T extends object>({
    contextData,
    userContext,
    platform,
    isClientSide = true,
    config,
    service,
}: ApiServiceArgs<T, Config> & { platform: PlatformID }): T {
    const client = createCrossPlatformApiClient({
        contextData,
        userContext,
        platform,
        isClientSide,
        fetchOptions: config,
    });

    return createProxyService(client, service);
}
